import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUpIcon, ZapIcon, GlobeIcon, HandshakeIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type WhyChamaleonProps = {
  language: Language;
};
const values = {
  ua: [{
    icon: TrendingUpIcon,
    title: 'Безперервно покращуємо сервіс',
    description: 'Постійний розвиток та вдосконалення процесів для найкращого досвіду клієнтів'
  }, {
    icon: ZapIcon,
    title: 'Сучасний підхід і швидкі рішення',
    description: 'Інноваційні технології та оперативне реагування на потреби ринку'
  }, {
    icon: GlobeIcon,
    title: 'Міжнародна команда',
    description: "Експерти з різних країн об'єднані спільною метою якості"
  }, {
    icon: HandshakeIcon,
    title: 'Партнерська підтримка B2B',
    description: 'Комплексна підтримка бізнес-партнерів на всіх етапах співпраці'
  }],
  en: [{
    icon: TrendingUpIcon,
    title: 'Continuously improving service',
    description: 'Constant development and process improvement for the best customer experience'
  }, {
    icon: ZapIcon,
    title: 'Modern approach & fast solutions',
    description: 'Innovative technologies and prompt response to market needs'
  }, {
    icon: GlobeIcon,
    title: 'International team',
    description: 'Experts from different countries united by a common goal of quality'
  }, {
    icon: HandshakeIcon,
    title: 'B2B partner support',
    description: 'Comprehensive support for business partners at all stages of cooperation'
  }]
};
const sectionText = {
  ua: {
    title: 'Чому CHAMALEON?'
  },
  en: {
    title: 'Why CHAMALEON?'
  }
};
export function WhyChamaleon({
  language
}: WhyChamaleonProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const valueList = values[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-341">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-342">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} data-id="element-343" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/2 left-0 w-px h-32 bg-gradient-to-b from-transparent via-chamaleon-yellow/50 to-transparent" data-id="element-344" />
      <div className="absolute top-1/2 right-0 w-px h-32 bg-gradient-to-b from-transparent via-chamaleon-yellow/50 to-transparent" data-id="element-345" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-347">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-348">
            {t.title}
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-349" />
        </motion.div>

        {/* Values Grid - Unified style with Bestsellers */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8" data-id="element-350">
          {valueList.map((value, index) => {
          const Icon = value.icon;
          return <motion.div key={value.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} whileHover={{
            y: -8
          }} className="group" data-id="element-351">
                <div className="glass-card rounded-xl sm:rounded-2xl p-6 sm:p-8 h-full transition-all duration-300 hover:border-chamaleon-yellow/30" data-id="element-352">
                  {/* Icon */}
                  <div className="relative mb-4 sm:mb-6 w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center" data-id="element-353">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl group-hover:from-chamaleon-yellow/30 group-hover:to-chamaleon-yellow/10 transition-all duration-300" data-id="element-354" />
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-chamaleon-yellow relative z-10" data-id="element-355" />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3 transition-colors" data-id="element-356">
                    {value.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed text-sm" data-id="element-357">
                    {value.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}